// Phase-3 Slice 27 + 28 + 36 — Zustand bridge for the rename preview dialog.
//
// Mirrors the `connectionInfoDialogStore` pattern (Slice 9 follow-up
// learning) — non-React callers (a future "Rename column…" context-menu
// handler) call `useRenamePreviewStore.getState().openPreview(input, deps?)`,
// which kicks off `previewRename(...)` and updates the store as the planner
// resolves. The mounted `<RenamePreviewDialog />` (next to
// `<ConnectionInfoDialog />` in `DatabaseExplorer.tsx`) reads from this
// store.
//
// Slice 28 adds `applyRename` for the dialog's Apply button. Status flow:
//   closed → loading → ready → applying → applied
//                                       ↘ apply-error
//   any state → closed (via close action)
//
// Slice 36 adds a `name-input` pre-step for table/view rename triggered from
// the Object Explorer context menu. The OE entry doesn't have Monaco's
// inline rename UI, so the dialog itself surfaces a name input field. Flow:
//   closed → name-input → loading → ready → ...
// `startNameInput` sets the source identifier + kind; `submitNewName` builds
// the full `PlanRenameInput` and routes through `openPreview`.

import type { DatabaseEngineId } from '@/core/engines';
import { create } from 'zustand';

import type { QualifiedName, RenameKind, RenameResult } from '../types';
import { trackNavFeature } from '../analytics/trackNavFeature';

import { databaseEngineIdToSqlEngine } from '../../workspace/visual-query-editor/edit/livePullSchemaSnapshot';
import { getMysqlLowerCaseTableNamesFor } from '../mysqlCaseProbe';

import { typedDestinationIdentifier } from './renameDestination';
import type { PlanRenameInput, PlanRenameResult } from './renamePlanner';
import {
  applyRename,
  previewRename,
  type ApplyRenameDeps,
  type PreviewRenameDeps,
} from './renameService';

type DialogStatus =
  | 'closed'
  | 'name-input'
  | 'loading'
  | 'ready'
  | 'error'
  | 'applying'
  | 'applied'
  | 'apply-error';

export type RenameNameInputIntent = {
  readonly connectionId: string;
  readonly database: string;
  readonly engineId: DatabaseEngineId;
  readonly kind: RenameKind;
  readonly fromIdentifier: QualifiedName;
  readonly containingTable?: QualifiedName;
};

/**
 * The planner's input plus the one thing only the INVOKING SURFACE knows: the
 * database the tab the rename was started from is bound to.
 *
 * Rename routes at the database the typed name RESOLVED to (`renameProvider.ts`
 * → `routedDatabase`), so `database` here can be a different database from the
 * one the editor tab sits in — `B.dbo.users` typed in an `A` tab plans, fetches
 * and ALTERs in `B`. The preview dialog says so when the two differ; nothing
 * else reads this, and the planner never routes by it, which is why it is not a
 * `PlanRenameInput` field. Surfaces where the two are the same by construction
 * (the Object Explorer's node) leave it undefined and the dialog stays silent.
 */
export type RenamePreviewInput = PlanRenameInput & {
  readonly tabDatabase?: string;
};

type RenamePreviewState = {
  readonly status: DialogStatus;
  readonly input: RenamePreviewInput | null;
  readonly result: PlanRenameResult | null;
  readonly applyResult: RenameResult | null;
  readonly errorMessage: string | null;
  readonly nameInputIntent: RenameNameInputIntent | null;
};

type RenamePreviewActions = {
  readonly openPreview: (input: RenamePreviewInput, deps?: PreviewRenameDeps) => Promise<void>;
  readonly startNameInput: (intent: RenameNameInputIntent) => void;
  readonly submitNewName: (newName: string, deps?: PreviewRenameDeps) => Promise<void>;
  /** #34 MUST-FIX 3: re-plan the current rename including/excluding the
   *  ambiguous (unqualified / 4-part linked-server) relation-reference
   *  rewrites. The preview surfaces an opt-in checkbox when
   *  `batch.ambiguousMatchCount > 0`; by default they are excluded from the
   *  applied set. No-ops when there is no current input. */
  readonly setIncludeAmbiguous: (enabled: boolean, deps?: PreviewRenameDeps) => Promise<void>;
  readonly applyBatch: (deps?: ApplyRenameDeps) => Promise<void>;
  readonly close: () => void;
};

type RenamePreviewStore = RenamePreviewState & RenamePreviewActions;

const initial: RenamePreviewState = {
  status: 'closed',
  input: null,
  result: null,
  applyResult: null,
  errorMessage: null,
  nameInputIntent: null,
};

export const useRenamePreviewStore = create<RenamePreviewStore>((set, get) => ({
  ...initial,

  openPreview: async (input, deps) => {
    set({
      status: 'loading',
      input,
      result: null,
      applyResult: null,
      errorMessage: null,
      nameInputIntent: null,
    });
    try {
      const result = await previewRename(input, deps);
      set({
        status: 'ready',
        input,
        result,
        applyResult: null,
        errorMessage: null,
        nameInputIntent: null,
      });
    } catch (err) {
      set({
        status: 'error',
        input,
        result: null,
        applyResult: null,
        errorMessage: err instanceof Error ? err.message : String(err),
        nameInputIntent: null,
      });
    }
  },

  startNameInput: (intent) => {
    set({
      status: 'name-input',
      input: null,
      result: null,
      applyResult: null,
      errorMessage: null,
      nameInputIntent: intent,
    });
  },

  submitNewName: async (newName, deps) => {
    const intent = get().nameInputIntent;
    if (!intent) return;
    const trimmed = newName.trim();
    if (trimmed.length === 0 || trimmed === intent.fromIdentifier.name) {
      set({
        status: 'error',
        errorMessage: 'New name must be different from the current name.',
      });
      return;
    }
    // The input takes free text, delimiters and all — `"MixedCase"` arrives
    // exactly as typed. `typedDestinationIdentifier` strips them ONCE (keeping
    // the intent on `quoting`) and folds what is left the way the engine folds
    // an unquoted/quoted identifier, so the schema ALTER delimits the decoded
    // name rather than wrapping the user's delimiters a second time. The same
    // decode runs on Monaco's F2 box in `renameProvider.ts`.
    const toIdentifier: QualifiedName = typedDestinationIdentifier(
      intent.fromIdentifier,
      trimmed,
      databaseEngineIdToSqlEngine(intent.engineId),
    );
    await get().openPreview(
      {
        connectionId: intent.connectionId,
        database: intent.database,
        request: {
          kind: intent.kind,
          fromIdentifier: intent.fromIdentifier,
          toIdentifier,
          engineId: intent.engineId,
          containingTable: intent.containingTable,
        },
        mysqlLowerCaseTableNames: getMysqlLowerCaseTableNamesFor(intent.connectionId, intent.engineId),
      },
      deps,
    );
  },

  setIncludeAmbiguous: async (enabled, deps) => {
    const { input } = get();
    if (!input) return;
    await get().openPreview({ ...input, includeAmbiguous: enabled }, deps);
  },

  applyBatch: async (deps) => {
    const { input, result } = get();
    if (!input || !result || result.status !== 'ok') return;
    set({ status: 'applying', applyResult: null, errorMessage: null });
    try {
      const applyResult = await applyRename(
        {
          connectionId: input.connectionId,
          database: input.database,
          batch: result.batch,
        },
        deps,
      );
      const status: DialogStatus = applyResult.status === 'success' ? 'applied' : 'apply-error';
      if (applyResult.status === 'success') {
        trackNavFeature({ name: 'feature:navfeatures_rename_apply', props: { kind: input.request.kind } });
      }
      set({
        status,
        applyResult,
        errorMessage: applyResult.errorMessage ?? null,
      });
    } catch (err) {
      set({
        status: 'apply-error',
        applyResult: null,
        errorMessage: err instanceof Error ? err.message : String(err),
      });
    }
  },

  close: () => set(initial),
}));
